import { useState, useEffect, useContext } from 'react';
import { doc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { FirebaseAuthContext } from '../context/FirebaseAuthContext';

export function useEmailInbox() {
  const authContext = useContext(FirebaseAuthContext);
  const user = authContext?.user;
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localEmailId, setLocalEmailId] = useState<string | null>(null);

  // Generate a unique email inbox ID for the user
  const generateEmailInboxId = async (): Promise<string | null> => {
    console.log('generateEmailInboxId called, user:', user);
    
    if (!user?.id) {
      console.log('No user ID found');
      setError('User not authenticated');
      return null;
    }

    if (user.emailInboxId || localEmailId) {
      // Email ID already exists, no need to generate
      console.log('Email ID already exists:', user.emailInboxId || localEmailId);
      return user.emailInboxId || localEmailId;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Generate a simple unique ID first
      const emailInboxId = generateUniqueId();
      console.log('Generated email ID:', emailInboxId);
      
      // Try to update the user document
      console.log('Updating user document for user ID:', user.id);
      const userDocRef = doc(firestore, 'users', user.id);
      
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userDocRef, {
        emailInboxId: emailInboxId,
      }, { merge: true });
      
      console.log('Successfully updated user document with email ID');

      setLocalEmailId(emailInboxId);
      setIsGenerating(false);
      return emailInboxId;
    } catch (err: any) {
      console.error('Error generating email inbox ID:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'Failed to generate email inbox ID');
      setIsGenerating(false);
      return null;
    }
  };

  // Generate a unique ID
  const generateUniqueId = (): string => {
    // Generate a random alphanumeric string
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  // Copy email address to clipboard
  const copyEmailAddress = async (): Promise<boolean> => {
    if (!user?.emailInboxId) {
      setError('No email inbox ID found');
      return false;
    }

    const emailAddress = `${user.emailInboxId}@inbound.tript.app`;
    
    try {
      if (navigator.clipboard) {
        // Web
        await navigator.clipboard.writeText(emailAddress);
        return true;
      } else {
        // React Native
        const Clipboard = await import('@react-native-clipboard/clipboard');
        Clipboard.default.setString(emailAddress);
        return true;
      }
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy email address');
      return false;
    }
  };

  // Use localEmailId if available while waiting for user context to update
  const effectiveEmailId = user?.emailInboxId || localEmailId;
  
  return {
    emailInboxId: effectiveEmailId,
    emailAddress: effectiveEmailId ? `${effectiveEmailId}@inbound.tript.app` : null,
    generateEmailInboxId,
    copyEmailAddress,
    isGenerating,
    error,
  };
}